import { useState, useCallback } from 'react';

interface MenuItem {
  label: string;
  id: string;
}

interface SearchResult {
  id: string;
  label: string;
  snippet: string;
  matchIndex: number;
}

const useSearch = (onMenuItemClick: (contentId: string) => void, menuItems: MenuItem[]) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const results = menuItems.reduce<SearchResult[]>((acc, item) => {
      const element = document.getElementById(item.id);
      if (!element) return acc;

      const text = element.textContent || '';
      const lowerText = text.toLowerCase();
      // Extract headings once per element instead of per match
      const headings = [...element.querySelectorAll('h1,h2,h3')].map((h) => ({
        text: h.textContent || '',
        pos: text.indexOf(h.textContent || ''),
      }));
      let searchFrom = 0;

      while (searchFrom < lowerText.length) {
        const index = lowerText.indexOf(lowerQuery, searchFrom);
        if (index === -1) break;

        const start = Math.max(0, index - 30);
        const end = Math.min(text.length, index + query.length + 30);
        const snippet = `${start > 0 ? '...' : ''}${text.slice(start, end)}${end < text.length ? '...' : ''}`;
        const label = headings.filter((h) => h.pos !== -1 && h.pos <= index).at(-1)?.text || item.label;

        acc.push({ id: item.id, label, snippet, matchIndex: index });
        searchFrom = index + query.length;
      }
      return acc;
    }, []);

    setSearchResults(results);
  }, [menuItems]);

  const handleSearchClick = useCallback((itemId: string, matchIndex = 0) => {
    onMenuItemClick(itemId);
    const query = searchQuery; // capture before state clear

    const element = document.getElementById(itemId);
    if (element && query) {
      setTimeout(() => {
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        let currentPos = 0;
        let node = walker.nextNode();
        while (node) {
          const nodeLength = (node.textContent || '').length;
          if (currentPos + nodeLength > matchIndex) {
            const range = document.createRange();
            const startOffset = matchIndex - currentPos;
            range.setStart(node, startOffset);
            range.setEnd(node, Math.min(startOffset + query.length, nodeLength));
            const rect = range.getBoundingClientRect();
            window.scrollBy({ top: rect.top - 120, behavior: 'smooth' });
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
            break;
          }
          currentPos += nodeLength;
          node = walker.nextNode();
        }
      }, 100);
    }

    setSearchQuery('');
    setSearchResults([]);
  }, [onMenuItemClick, searchQuery]);

  return {
    searchQuery,
    searchResults,
    handleSearch,
    handleSearchClick,
  };
};

export default useSearch;
